import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Archive, Search, Download, RefreshCw, Calendar, Filter, X,
  MessageSquare, Smartphone, CheckCircle, XCircle, Eye, Clock,
  TrendingUp, Send, Users, BarChart3, FileText, ChevronRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-slate-500', icon: Clock },
  sent: { label: 'Envoyé', color: 'bg-blue-500', icon: Send },
  delivered: { label: 'Délivré', color: 'bg-green-500', icon: CheckCircle },
  read: { label: 'Lu', color: 'bg-emerald-500', icon: Eye },
  failed: { label: 'Échec', color: 'bg-red-500', icon: XCircle },
  fallback_sms: { label: 'Fallback SMS', color: 'bg-amber-500', icon: MessageSquare }
};

const channelConfig = {
  rcs: { label: 'RCS', color: 'bg-indigo-500', icon: Smartphone },
  sms: { label: 'SMS', color: 'bg-amber-500', icon: MessageSquare },
  sms_fallback: { label: 'SMS Fallback', color: 'bg-orange-500', icon: MessageSquare }
};

export default function Archives() {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [period, setPeriod] = useState('30d');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        sort_by: 'sent_at',
        sort_order: 'desc'
      });
      
      if (filterChannel !== 'all') params.append('channel', filterChannel);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      if (searchPhone) params.append('phone_search', searchPhone);
      
      const response = await fetch(`${API_URL}/api/v2/archives/messages?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token, page, filterChannel, filterStatus, filterDateFrom, filterDateTo, searchPhone]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '20',
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      
      const response = await fetch(`${API_URL}/api/v2/archives/campaigns?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/archives/stats/overview?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [token, period]);

  const fetchDailyStats = useCallback(async () => {
    try {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
      const response = await fetch(`${API_URL}/api/v2/archives/stats/daily?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDailyStats(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching daily stats:', err);
    }
  }, [token, period]);

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages();
    } else if (activeTab === 'campaigns') {
      fetchCampaigns();
    }
    fetchStats();
    fetchDailyStats();
  }, [activeTab, fetchMessages, fetchCampaigns, fetchStats, fetchDailyStats]);

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ format, limit: '10000' });
      if (filterChannel !== 'all') params.append('channel', filterChannel);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await fetch(`${API_URL}/api/v2/archives/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (format === 'csv') {
          const blob = new Blob([data.csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `archives_${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
        } else {
          const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `archives_${new Date().toISOString().split('T')[0]}.json`;
          a.click();
        }
        
        toast.success(`Export ${format.toUpperCase()} téléchargé (${data.total_records} enregistrements)`);
      }
    } catch (err) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const resetFilters = () => {
    setFilterChannel('all');
    setFilterStatus('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchPhone('');
    setPage(1);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="archives-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Archive className="inline-block w-7 h-7 mr-2 text-indigo-500" />
              Archives
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Historique complet des envois RCS et SMS
            </p>
          </div>
          
          <div className="flex gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 jours</SelectItem>
                <SelectItem value="30d">30 jours</SelectItem>
                <SelectItem value="90d">90 jours</SelectItem>
                <SelectItem value="365d">1 an</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={() => handleExport('csv')}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-4 pb-3">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total envoyés</p>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {stats.total_messages?.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-4 pb-3">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>RCS</p>
                <p className="text-2xl font-bold text-indigo-500">{stats.rcs_count?.toLocaleString()}</p>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-4 pb-3">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>SMS / Fallback</p>
                <p className="text-2xl font-bold text-amber-500">{stats.sms_count?.toLocaleString()}</p>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-4 pb-3">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Taux délivré</p>
                <p className="text-2xl font-bold text-green-500">{stats.delivery_rate}%</p>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-4 pb-3">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Taux lu</p>
                <p className="text-2xl font-bold text-emerald-500">{stats.read_rate}%</p>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-4 pb-3">
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Échecs</p>
                <p className="text-2xl font-bold text-red-500">{stats.failed_count?.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Chart */}
        {dailyStats.length > 0 && (
          <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                <TrendingUp className="inline-block w-5 h-5 mr-2" />
                Volume d'envois
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    />
                    <YAxis tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                        border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(v) => new Date(v).toLocaleDateString('fr-FR')}
                    />
                    <Legend />
                    <Bar dataKey="rcs" name="RCS" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="sms" name="SMS" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={isDarkMode ? 'bg-slate-800' : ''}>
            <TabsTrigger value="messages" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Campagnes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="messages" className="space-y-4">
            {/* Filters */}
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <Label>Rechercher</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value)}
                        placeholder="Numéro..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="w-36">
                    <Label>Canal</Label>
                    <Select value={filterChannel} onValueChange={setFilterChannel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="rcs">RCS</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="sms_fallback">SMS Fallback</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-36">
                    <Label>Statut</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="delivered">Délivré</SelectItem>
                        <SelectItem value="read">Lu</SelectItem>
                        <SelectItem value="sent">Envoyé</SelectItem>
                        <SelectItem value="failed">Échec</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-36">
                    <Label>Du</Label>
                    <Input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </div>
                  
                  <div className="w-36">
                    <Label>Au</Label>
                    <Input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </div>
                  
                  <Button variant="outline" onClick={resetFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Réinitialiser
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Messages Table */}
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardHeader>
                <CardTitle className={isDarkMode ? 'text-white' : ''}>
                  Messages ({total.toLocaleString()})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8">
                    <Archive className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                      Aucun message trouvé
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                          <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Destinataire</th>
                          <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Canal</th>
                          <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Statut</th>
                          <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Type</th>
                          <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {messages.map((msg) => {
                          const StatusIcon = statusConfig[msg.status]?.icon || Clock;
                          const ChannelIcon = channelConfig[msg.channel]?.icon || MessageSquare;
                          
                          return (
                            <tr 
                              key={msg.id}
                              className={`border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-100 hover:bg-slate-50'}`}
                            >
                              <td className={`py-3 px-4 font-mono text-sm ${isDarkMode ? 'text-white' : ''}`}>
                                {msg.recipient_phone}
                              </td>
                              <td className="py-3 px-4">
                                <Badge className={`${channelConfig[msg.channel]?.color || 'bg-slate-500'} text-white`}>
                                  <ChannelIcon className="w-3 h-3 mr-1" />
                                  {channelConfig[msg.channel]?.label || msg.channel}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant="outline" className={`${isDarkMode ? 'border-slate-600' : ''}`}>
                                  <StatusIcon className={`w-3 h-3 mr-1 ${
                                    msg.status === 'delivered' || msg.status === 'read' ? 'text-green-500' :
                                    msg.status === 'failed' ? 'text-red-500' : 'text-slate-400'
                                  }`} />
                                  {statusConfig[msg.status]?.label || msg.status}
                                </Badge>
                              </td>
                              <td className={`py-3 px-4 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                {msg.message_type || '-'}
                              </td>
                              <td className={`py-3 px-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {msg.sent_at ? new Date(msg.sent_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      Page {page} sur {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        Précédent
                      </Button>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="campaigns" className="space-y-4">
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardHeader>
                <CardTitle className={isDarkMode ? 'text-white' : ''}>
                  Campagnes archivées
                </CardTitle>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                      Aucune campagne archivée
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map((campaign) => (
                      <div 
                        key={campaign.id}
                        className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {campaign.name}
                            </h4>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              Créée le {new Date(campaign.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          
                          {campaign.stats && (
                            <div className="flex gap-4 text-sm">
                              <div className="text-center">
                                <p className={`font-bold ${isDarkMode ? 'text-white' : ''}`}>{campaign.stats.total}</p>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total</p>
                              </div>
                              <div className="text-center">
                                <p className="font-bold text-green-500">{campaign.stats.delivery_rate}%</p>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Délivré</p>
                              </div>
                              <div className="text-center">
                                <p className="font-bold text-emerald-500">{campaign.stats.read_rate}%</p>
                                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Lu</p>
                              </div>
                            </div>
                          )}
                          
                          <ChevronRight className={`w-5 h-5 ml-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
